import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from "react";
import { HeyatYoluContext } from "../../App";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet";
import "./styles/Account.css";

// Constants
const PAGE_SIZE = 12;
const API_DELAY = 300;
const INTERSECTION_MARGIN = "200px";

// Validation patterns
const PASSWORD_MIN_LENGTH = 6;
const PASSWORD_VALIDATION = {
  minLength: PASSWORD_MIN_LENGTH,
  hasUpperCase: /[A-Z]/,
  hasLowerCase: /[a-z]/,
  hasNumber: /\d/,
  hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/
};

function Account() {
  const { user, paymentAmount, logout } = useContext(HeyatYoluContext);
  const navigate = useNavigate();

  // Profile management states
  const [profiles, setProfiles] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // UI states
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [payingProfileId, setPayingProfileId] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  // Password form states
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [passwordErrors, setPasswordErrors] = useState({});
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // Intersection Observer for infinite scroll
  const observer = useRef();
  const lastProfileRef = useCallback((node) => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        setPage(prev => prev + 1);
      }
    }, { 
      rootMargin: INTERSECTION_MARGIN,
      threshold: 0.1
    });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // Validate password
  const validatePassword = useCallback((password) => {
    const errors = {};
    
    if (!password) {
      errors.required = "Şifrə daxil edilməlidir";
      return errors;
    }
    
    if (password.length < PASSWORD_VALIDATION.minLength) {
      errors.minLength = `Şifrə ən azı ${PASSWORD_VALIDATION.minLength} simvol olmalıdır`;
    }
    
    if (!PASSWORD_VALIDATION.hasUpperCase.test(password)) {
      errors.uppercase = "Şifrədə ən azı bir böyük hərf olmalıdır";
    }
    
    if (!PASSWORD_VALIDATION.hasLowerCase.test(password)) {
      errors.lowercase = "Şifrədə ən azı bir kiçik hərf olmalıdır";
    }
    
    if (!PASSWORD_VALIDATION.hasNumber.test(password)) {
      errors.number = "Şifrədə ən azı bir rəqəm olmalıdır";
    }
    
    return errors;
  }, []);

  // Fetch profiles with error handling and caching
  const fetchProfiles = useCallback(async (pageNum = 1, reset = false) => {
    if (!user?.id) return;
    
    setLoading(true);
    
    try {
      const response = await api.get(`/memorialprofile/by-user`, {
        params: {
          page: pageNum,
          pageSize: PAGE_SIZE
        }
      });

      if (response.data) {
        const { items, totalCount: newTotalCount } = response.data;
        
        setProfiles(prev => reset ? items : [...prev, ...items]);
        setTotalCount(newTotalCount);
        setHasMore(items.length === PAGE_SIZE && (pageNum * PAGE_SIZE) < newTotalCount);
        
        if (pageNum === 1) {
          setInitialLoading(false);
        }
      }
    } catch (error) {
      console.error("Profile fetch error:", error);
      const errorMessage = error.response?.data?.message || "Profillər yüklənə bilmədi";
      toast.error(errorMessage);
      
      if (pageNum === 1) {
        setInitialLoading(false);
      }
    } finally {
      setTimeout(() => setLoading(false), API_DELAY);
    }
  }, [user?.id]);

  // Handle profile deletion
  const handleDelete = useCallback(async () => {
    if (!confirmDeleteId) return;
    
    try {
      await api.delete(`/memorialprofile/${confirmDeleteId}`);
      
      // Optimistic update
      setProfiles(prev => prev.filter(p => p.id !== confirmDeleteId));
      setTotalCount(prev => prev - 1);
      
      toast.success("Profil uğurla silindi");
      
      // Refresh if no profiles left
      if (profiles.length === 1) {
        setPage(1);
        setHasMore(true);
        fetchProfiles(1, true);
      }
    } catch (error) {
      console.error("Delete error:", error);
      const errorMessage = error.response?.data?.message || "Profil silinə bilmədi";
      toast.error(errorMessage);
    } finally {
      setConfirmDeleteId(null);
    }
  }, [confirmDeleteId, profiles.length, fetchProfiles]);

  // Handle payment
  const startPayment = useCallback(async (profileId) => {
    if (!profileId || !user?.id || !paymentAmount) {
      toast.error("Ödəniş məlumatları tapılmadı");
      return;
    }
    
    try {
      const response = await api.post("/payment/create-payriff-order", {
        amount: paymentAmount,
        profileId,
        userId: user.id
      });

      const paymentUrl = response.data?.paymentUrl;
      if (paymentUrl) {
        window.location.href = paymentUrl;
      } else {
        throw new Error("Payment URL not received");
      }
    } catch (error) {
      console.error("Payment error:", error);
      const errorMessage = error.response?.data?.message || "Ödəniş zamanı xəta baş verdi";
      toast.error(errorMessage);
    }
  }, [user?.id, paymentAmount]);

  // Handle password change
  const handlePasswordChange = useCallback(async (e) => {
    e.preventDefault();
    
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    const errors = {};

    if (!currentPassword.trim()) {
      errors.currentPassword = "Cari şifrə daxil edilməlidir";
    }

    const newPasswordErrors = validatePassword(newPassword);
    if (Object.keys(newPasswordErrors).length > 0) {
      errors.newPassword = Object.values(newPasswordErrors)[0];
    }

    if (newPassword !== confirmPassword) {
      errors.confirmPassword = "Şifrələr uyğun gəlmir";
    }

    if (Object.keys(errors).length > 0) {
      setPasswordErrors(errors);
      return;
    }

    setPasswordLoading(true);
    setPasswordErrors({});
    setPasswordSuccess("");

    try {
      await api.post("/account/change-password", {
        currentPassword,
        newPassword,
      });

      setPasswordSuccess("Şifrə uğurla dəyişdirildi!");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
      });

      setTimeout(() => {
        setShowPasswordForm(false);
        setPasswordSuccess("");
      }, 2000);

    } catch (error) {
      console.error("Password change error:", error);
      const errorMessage = error.response?.data?.[0]?.description || 
                          error.response?.data?.message || 
                          "Şifrə dəyişdirilə bilmədi";
      setPasswordErrors({ general: errorMessage });
    } finally {
      setPasswordLoading(false);
    }
  }, [passwordForm, validatePassword]);

  // Handle logout
  const handleLogout = useCallback(() => {
    if (logout) {
      logout();
    } else {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
    }
    navigate("/giris");
  }, [logout, navigate]);

  // Password form input handler
  const handlePasswordInputChange = useCallback((field, value) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    
    if (passwordErrors[field]) {
      setPasswordErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [passwordErrors]);

  // Initial data load
  useEffect(() => {
    if (user?.id) {
      setProfiles([]);
      setPage(1);
      setHasMore(true);
      setTotalCount(0);
      fetchProfiles(1, true);
    }
  }, [user?.id, fetchProfiles]);

  // Load more profiles when page changes
  useEffect(() => {
    if (page > 1 && user?.id) {
      fetchProfiles(page, false);
    }
  }, [page, user?.id, fetchProfiles]);

  // Cleanup observer
  useEffect(() => {
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  // Memoized profile status
// Memoized profile status
  const getProfileStatus = useMemo(() => (profile) => {
    // Şəhid profili — admin təsdiqi gözləyir
    if (profile.isMartyr && !profile.isConfirmed) {
      return {
        text: "Admin təsdiqini gözləyir",
        className: "acc-status-waiting",
        icon: "⏳"
      };
    }

    // Adi profil — ödəniş edilməyib
    if (!profile.isMartyr && !profile.isPaid) {
      return {
        text: "Ödəniş edilməlidir",
        className: "acc-status-unpaid",
        icon: "💳"
      };
    }

    // ← YENİ: Adi profil — ödəniş tələb olunmayıb (pulsuz rejim), admin təsdiqi gözləyir
    if (!profile.isMartyr && profile.isPaid && !profile.isConfirmed) {
      return {
        text: "Admin təsdiqini gözləyir",
        className: "acc-status-waiting",
        icon: "⏳"
      };
    }

    return {
      text: "Yayımlanıb",
      className: "acc-status-published",
      icon: "✅"
    };
  }, []);

  // Render loading skeleton
  const renderLoadingSkeleton = () => (
    <div className="acc-profile-loading">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="acc-skeleton-card" />
      ))}
    </div>
  );

  // Render password form
  const renderPasswordForm = () => (
    <div className="acc-password-form-wrapper">
      <div className="acc-password-form-header">
        <h3>🔐 Şifrəni Dəyiş</h3>
        <button 
          type="button" 
          className="acc-close-btn"
          onClick={() => {
            setShowPasswordForm(false);
            setPasswordErrors({});
            setPasswordSuccess("");
            setPasswordForm({
              currentPassword: "",
              newPassword: "",
              confirmPassword: ""
            });
          }}
          aria-label="Bağla"
        >
          ✕
        </button>
      </div>

      <form onSubmit={handlePasswordChange} className="acc-password-form">
        <div className="acc-form-group">
          <label>Cari şifrə *</label>
          <input
            type="password"
            value={passwordForm.currentPassword}
            onChange={(e) => handlePasswordInputChange('currentPassword', e.target.value)}
            className={passwordErrors.currentPassword ? 'acc-input-error' : ''}
            required
            autoComplete="current-password"
          />
          {passwordErrors.currentPassword && (
            <span className="acc-error-message">{passwordErrors.currentPassword}</span>
          )}
        </div>

        <div className="acc-form-group">
          <label>Yeni şifrə *</label>
          <input
            type="password"
            value={passwordForm.newPassword}
            onChange={(e) => handlePasswordInputChange('newPassword', e.target.value)}
            className={passwordErrors.newPassword ? 'acc-input-error' : ''}
            required
            autoComplete="new-password"
          />
          {passwordErrors.newPassword && (
            <span className="acc-error-message">{passwordErrors.newPassword}</span>
          )}
          <div className="acc-password-requirements">
            <small>Şifrə tələbləri: ən azı 6 simvol, böyük hərf, kiçik hərf və rəqəm</small>
          </div>
        </div>

        <div className="acc-form-group">
          <label>Yeni şifrəni təsdiqlə *</label>
          <input
            type="password"
            value={passwordForm.confirmPassword}
            onChange={(e) => handlePasswordInputChange('confirmPassword', e.target.value)}
            className={passwordErrors.confirmPassword ? 'acc-input-error' : ''}
            required
            autoComplete="new-password"
          />
          {passwordErrors.confirmPassword && (
            <span className="acc-error-message">{passwordErrors.confirmPassword}</span>
          )}
        </div>

        {passwordErrors.general && (
          <div className="acc-error-message acc-general-error">{passwordErrors.general}</div>
        )}

        {passwordSuccess && (
          <div className="acc-success-message">{passwordSuccess}</div>
        )}

        <div className="acc-form-actions">
          <button 
            type="submit" 
            className="acc-btn-primary"
            disabled={passwordLoading}
          >
            {passwordLoading ? "Dəyişdirilir..." : "Şifrəni Dəyiş"}
          </button>
          <button 
            type="button" 
            className="acc-btn-secondary"
            onClick={() => setShowPasswordForm(false)}
          >
            Ləğv et
          </button>
        </div>
      </form>
    </div>
  );

  // Render profile card
  const renderProfileCard = (profile, index) => {
    const isLast = index === profiles.length - 1;
    const status = getProfileStatus(profile);

    return (
      <div
        key={profile.id}
        ref={isLast ? lastProfileRef : null}
        className="acc-profile-card"
      >
        {profile.photoUrl && (
          <div className="acc-profile-image-container">
            <img
              src={`https://api.heyatyolu.az/${profile.photoUrl}`}
              alt={`${profile.firstName} ${profile.lastName}`}
              className="acc-profile-photo"
              loading="lazy"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        <div className="acc-profile-info">
          <h4 className="acc-profile-name">
            {profile.firstName} {profile.lastName}
            {profile.isMartyr && <span className="acc-martyr-badge">🕊️ Şəhid</span>}
          </h4>

          <div className="acc-profile-dates">
            <p><strong>Doğum:</strong> {new Date(profile.dateOfBirth).toLocaleDateString('az-AZ')}</p>
            <p><strong>Vəfat:</strong> {new Date(profile.dateOfDeath).toLocaleDateString('az-AZ')}</p>
          </div>

          <div className="acc-profile-status">
            <span className={`acc-status-badge ${status.className}`}>
              {status.icon} {status.text}
            </span>
          </div>

          {!profile.isMartyr && !profile.isPaid && (
            <div className="acc-payment-section">
              <button
                className="acc-btn-payment"
                onClick={() => startPayment(profile.id)}
              >
                💳 Ödəniş et ({paymentAmount} AZN)
              </button>
            </div>
          )}

          <div className="acc-profile-actions">
            <button 
              className="acc-btn-view"
              onClick={() => navigate(`/merhum/${profile.id}`)}
              title="Profili görüntülə"
            >
              👁️ Bax
            </button>
            <button 
              className="acc-btn-edit"
              onClick={() => navigate(`/redakte-et/${profile.id}`)}
              title="Profili redaktə et"
            >
              ✏️ Redaktə
            </button>
            <button 
              className="acc-btn-delete"
              onClick={() => setConfirmDeleteId(profile.id)}
              title="Profili sil"
            >
              🗑️ Sil
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Main render
  return (
    <div className="acc-container">
      <Helmet>
        <title>Şəxsi Kabinet – HeyatYolu</title>
        <meta
          name="description"
          content="Paylaşdığınız xatirə profillərini idarə edin. Profil statusuna baxın, ödəniş həyata keçirin, redaktə edin və ya silin."
        />
        <meta name="keywords" content="şəxsi kabinet, profil idarəetmə, xatirə, ödəniş, heyatyolu" />
        <meta property="og:title" content="Şəxsi Kabinet – HeyatYolu" />
        <meta property="og:description" content="Xatirə profillərini idarə edin və statuslarını izləyin." />
        <meta property="og:type" content="website" />
      </Helmet>

      <header className="acc-header">
        <h1>👤 Şəxsi Kabinet</h1>
        <p>Paylaşdığınız xatirə profillərini idarə edin</p>
      </header>

      {/* User Info Section */}
      <section className="acc-user-section">
        <div className="acc-user-card">
          <div className="acc-user-avatar">
            <span>{user?.fullName?.charAt(0)?.toUpperCase() || "👤"}</span>
          </div>
          <div className="acc-user-details">
            <h2 className="acc-user-fullname">{user?.fullName}</h2>
            <p className="acc-user-contact">{user?.email || user?.phoneNumber}</p>
            <div className="acc-user-meta">
              <p><strong>İstifadəçi ID:</strong> {user?.id}</p>
              <p><strong>Qeydiyyat tarixi:</strong> {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('az-AZ') : 'Məlum deyil'}</p>
            </div>
          </div>
        </div>

        <div className="acc-user-actions">
          <button
            onClick={() => setShowPasswordForm(true)}
            className="acc-btn-change-password"
          >
            🔐 Parolu Dəyiş
          </button>
          <button 
            onClick={handleLogout} 
            className="acc-btn-logout"
          >
            🚪 Çıxış
          </button>
        </div>
      </section>

      {/* Password Form */}
      {showPasswordForm && renderPasswordForm()}

      {/* Profiles Section */}
      <section className="acc-profiles-section">
        <div className="acc-section-header">
          <h2>📋 Paylaşdığınız Profillər</h2>
          {totalCount > 0 && (
            <span className="acc-profile-count">Cəmi: {totalCount} profil</span>
          )}
        </div>

        {initialLoading ? (
          renderLoadingSkeleton()
        ) : profiles.length === 0 ? (
          <div className="acc-empty-state">
            <div className="acc-empty-icon">📝</div>
            <h3>Hələ heç bir profil paylaşmamısınız</h3>
            <p>İlk xatirə profilinizi yaratmaq üçün başlayın</p>
            <button 
              className="acc-btn-create-profile"
              onClick={() => navigate('/xatire-yarat')}
            >
              ➕ İlk Profili Yarat
            </button>
          </div>
        ) : (
          <>
            <div className="acc-profile-grid">
              {profiles.map((profile, index) => renderProfileCard(profile, index))}
            </div>

            {loading && profiles.length > 0 && (
              <div className="acc-loading-more">
                <div className="acc-spinner-small"></div>
                <span>Daha çox profil yüklənir...</span>
              </div>
            )}

            {!hasMore && profiles.length > 0 && (
              <div className="acc-end-message">
                <p>Bütün profillər yükləndi</p>
              </div>
            )}
          </>
        )}
      </section>

      {/* Delete Confirmation Modal */}
      {confirmDeleteId && (
        <div className="acc-modal-overlay">
          <div className="acc-confirm-dialog">
            <div className="acc-confirm-header">
              <h3>⚠️ Profili Sil</h3>
            </div>
            <div className="acc-confirm-body">
              <p>Bu profili silmək istədiyinizə əminsiniz?</p>
              <p><strong>Diqqət:</strong> Bu əməliyyat geri alına bilməz.</p>
            </div>
            <div className="acc-confirm-actions">
              <button 
                className="acc-btn-danger" 
                onClick={handleDelete}
              >
                Bəli, Sil
              </button>
              <button 
                className="acc-btn-cancel"
                onClick={() => setConfirmDeleteId(null)}
              >
                Ləğv et
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Account;